import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useInteriorDesign } from "./useHook.js";
import { ImageWithFallback } from "../../Utils/loader";
import Swal from "sweetalert2";
import InteriorDesignModal from "../../Components/Modals/InteriorDesign/InteriorDesignModal.jsx";
import Loader from "../../Utils/loader";

const InteriorDesignDetails = () => {
  const { id } = useParams();
  const { getInteriorDesignById, updateInteriorDesign, showInteriorDesignOnHomeScreen } = useInteriorDesign();
  const [interiorData, setInteriorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false);

  useEffect(() => {
    loadInteriorDesignDetails();
  }, [id]);

  const loadInteriorDesignDetails = async () => {
    try {
      setLoading(true);
      await getInteriorDesignById(id, setInteriorData);
    } catch (error) {
      console.error("Error loading interior design details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load interior design details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (interiorData) {
      const transformedData = {
        title: interiorData.title || "",
        plot_dimension: interiorData.plot_dimension || "",
        covered_area: interiorData.covered_area || "",
        floors: interiorData.floors || 0,
        bed_rooms: interiorData.bed_rooms || 0,
        bath_rooms: interiorData.bath_rooms || 0,
        customUrl: interiorData.custom_url || interiorData.customUrl || "",
        description: interiorData.description || "",
        category_id: interiorData.category?.id || "",
        room_size: interiorData.room_size || "",
        design_type: interiorData.design_type || "",
        images: [],
        existingImages: interiorData.images || [],
        features: {
          carParking: interiorData.car_parking || false,
          terrace: interiorData.terrace || false,
          servantQuarter: interiorData.servant_quarters || false,
        }
      };
      setEditData(transformedData);
    }
    setShowEditModal(true);
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "images" && key !== "imagePreviews" && key !== "primaryImageIndex") {
          if (key === "features") {
            apiData.append(key, JSON.stringify(formData[key]));
          } else {
            apiData.append(key, formData[key]);
          }
        }
      });

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
        });
      }

      if (formData.existingImages) {
        formData.existingImages.forEach((image) => {
          apiData.append("existing_images", JSON.stringify(image));
        });
      }

      await updateInteriorDesign(id, apiData);
      await loadInteriorDesignDetails();
      setShowEditModal(false);
      setEditData(null);

      Swal.fire({
        icon: "success",
        title: "Interior Design Updated",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to update interior design:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update interior design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHomeScreenItem = async () => {
    if (!interiorData?.id) return;

    setIsHomeScreenLoading(true);
    const newStatus = !interiorData.homeScreen;

    try {
      await showInteriorDesignOnHomeScreen(interiorData.id, newStatus ? 1 : 0);
      await loadInteriorDesignDetails();
      Swal.fire({
        title: "Success",
        text: newStatus
          ? "Interior design has been added to home screen"
          : "Interior design has been removed from home screen",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update home screen status:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update home screen status. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setIsHomeScreenLoading(false);
    }
  };

  if (loading) {
    return <Loader message="Loading interior design details..." />;
  }

  if (!interiorData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Interior Design Not Found</h2>
          <p className="text-gray-500">The interior design you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Top Header */}
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Interior Design Details</h2>
        </div>
        <div className="flex space-x-4">
          <button
            className={`py-2 px-4 rounded flex items-center space-x-2 ${isHomeScreenLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
              } text-white`}
            onClick={handleHomeScreenItem}
            disabled={isHomeScreenLoading}
          >
            {isHomeScreenLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Updating...</span>
              </>
            ) : (
              <span>
                {interiorData?.homeScreen
                  ? "Hide from home screen"
                  : "Home page Priority"}
              </span>
            )}
          </button>
          <button
            onClick={handleEditClick}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="mb-6">
          <h2 className="text-3xl font-bold">{interiorData.title}</h2>
          <p className="text-gray-600 mt-1">Category: {interiorData.category?.name}</p>
          <p className="text-sm text-gray-500 mt-2">
            Created: {new Date(interiorData.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {/* <p className="mb-2">
            <span className="font-semibold">Plot Dimension: </span>
            {interiorData.plot_dimension}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Covered Area: </span>
            {interiorData.covered_area}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Floors: </span>
            {interiorData.floors}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Bedrooms: </span>
            {interiorData.bed_rooms}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Bathrooms: </span>
            {interiorData.bath_rooms}
          </p> */}
            <p className="mb-2">
              <span className="font-semibold">Room Size: </span>
              {interiorData.room_size}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Design Type: </span>
              {interiorData.design_type}
            </p>
          </div>

          {/* <div>
          <p className="mb-2">
            <span className="font-semibold">Car Parking: </span>
            {interiorData.car_parking ? "Yes" : "No"}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Servant Quarters: </span>
            {interiorData.servant_quarters ? "Yes" : "No"}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Terrace: </span>
            {interiorData.terrace ? "Yes" : "No"}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Room Size: </span>
            {interiorData.room_size}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Design Type: </span>
            {interiorData.design_type}
          </p>
        </div> */}
        </div>

        <div className="mt-6">
          <div className="flex border-b border-gray-300 mb-4">
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${activeTab === "description"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-600"
                }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${activeTab === "images"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-600"
                }`}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
          </div>

          {activeTab === "description" && (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: interiorData.description }}
            />
          )}

          {activeTab === "images" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {interiorData.images?.map((img, index) => (
                <div key={img.id || index} className="relative">
                  <ImageWithFallback
                    src={img.image_url}
                    alt={`Interior Design ${index + 1}`}
                    className="w-full h-48 object-cover rounded shadow-md"
                  />
                  {img.is_primary && (
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      Primary
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <InteriorDesignModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditData(null);
          }}
          onSubmit={handleSubmit}
          isEditing={true}
          editData={editData}
          isSubmitting={isSubmitting}
        />
      </div>
    </>
  );
};

export default InteriorDesignDetails;
